import { get, set, keys, delMany } from 'idb-keyval';

// Best-effort local cache for the current gig's set list / requests / lyrics,
// so a musician's on-stage view survives a spotty or dead venue connection.
// IndexedDB is unavailable in some contexts (private browsing, older WebViews) -
// every call is wrapped so caching failures never break the live app.

const PREFIX = 'gigrequest-cache:';

export async function cacheData(name, data) {
  try {
    await set(PREFIX + name, { data, cachedAt: Date.now() });
  } catch {
    // ignore - caching is best-effort only
  }
}

// Called on sign-out so the next person on a shared device can't see this band's data.
export async function clearCachedData() {
  try {
    const ours = (await keys()).filter(k => typeof k === 'string' && k.startsWith(PREFIX));
    await delMany(ours);
  } catch {
    // ignore - caching is best-effort only
  }
}

export async function getCachedData(name) {
  try {
    return (await get(PREFIX + name)) || null;
  } catch {
    return null;
  }
}

// How long a page with nothing on screen waits for the server before showing its saved copy
const SAVED_COPY_AFTER_MS = 500;

// Loads a page's live data, with its saved copy as the fallback. Without signal an iPhone can take
// several seconds to fail a request, so a page with nothing on screen yet (`emptyPage`) shows the
// saved copy if the server hasn't answered within half a second, and swaps in live data if it does.
// Refetches after an edit leave `emptyPage` off, so the copy saved before the edit never flashes up.
//  - load() fetches the live data, which is saved under `name` and passed to show()
//  - resolves to null for live data, or the saved copy's cachedAt when only that could be shown
//  - rejects with load()'s error when there's no saved copy, or when the server answered with a
//    4xx (a definite "no", e.g. the gig is gone), since a saved copy would hide that
export async function loadWithSavedCopy(name, load, show, { emptyPage = false } = {}) {
  let settled = false;
  let shownSaved = false;
  const saved = getCachedData(name);
  if (emptyPage) {
    const wait = navigator.onLine === false ? 0 : SAVED_COPY_AFTER_MS;
    setTimeout(async () => {
      const copy = await saved;
      if (copy && !settled) {
        shownSaved = true;
        show(copy.data);
      }
    }, wait);
  }
  try {
    const data = await load();
    settled = true;
    show(data);
    if (data !== undefined) cacheData(name, data);
    return null;
  } catch (err) {
    settled = true;
    const status = err.response?.status;
    const copy = await saved;
    if (!copy || (status >= 400 && status < 500)) throw err;
    if (!shownSaved) show(copy.data);
    return copy.cachedAt;
  }
}
